import "./Legal.scss";

export default function TermsOfService() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last Updated: March 9, 2026</p>

        <section className="legal-section">
          <h2>1. Agreement to Terms</h2>
          <p>
            By accessing and using SOMIGO ("the Service"), provided by
            Happyhill, based in Aarhus, Denmark ("we," "our," or "us"), you
            agree to be bound by these Terms of Service. If you do not agree to
            these terms, please do not use our Service.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Description of Service</h2>
          <p>
            SOMIGO is a web-based project management and task organization
            platform that helps individuals and teams organize their work
            through tasks, projects, notes, and collaboration features. The
            Service is accessible via web browsers and mobile applications.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. Account Registration</h2>
          <p>To use certain features of the Service, you must:</p>
          <ul>
            <li>Create an account with accurate and complete information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Be responsible for all activities under your account</li>
            <li>Notify us immediately of any unauthorized access</li>
          </ul>
          <p>
            You are solely responsible for maintaining the confidentiality of
            your password and account information.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. Free Trial and Subscription</h2>
          <h3>4.1 Free Trial</h3>
          <p>
            We offer a <strong>14-day free trial</strong> that requires a valid
            credit card. You will not be charged during the trial period. If you
            do not cancel before the trial ends, your subscription will
            automatically begin, and your payment method will be charged.
          </p>

          <h3>4.2 Subscription Fees</h3>
          <p>
            After your free trial, you will be charged the subscription fee
            according to the plan you selected ($8 per user per month, or as
            displayed at the time of purchase). All fees are in USD unless
            otherwise stated.
          </p>

          <h3>4.3 Billing</h3>
          <ul>
            <li>
              Subscriptions are billed monthly in advance and automatically
              renew
            </li>
            <li>You authorize us to charge your payment method on file</li>
            <li>
              Price changes will be communicated at least 30 days in advance
            </li>
            <li>You can cancel your subscription at any time</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. Cancellation and Refunds</h2>
          <h3>5.1 Cancellation</h3>
          <p>
            You may cancel your subscription at any time from your account
            settings. Upon cancellation:
          </p>
          <ul>
            <li>
              You will retain access until the end of your current billing
              period
            </li>
            <li>You will not be charged for subsequent billing periods</li>
            <li>
              Your data will remain accessible for 30 days after cancellation
            </li>
          </ul>

          <h3>5.2 Refund Policy</h3>
          <p>
            <strong>General Policy:</strong> We do not provide refunds for
            partial subscription periods. If you cancel mid-billing cycle, you
            will have access until the end of that period, but no refund will be
            issued for the unused portion.
          </p>
          <p>
            <strong>Exceptions:</strong> Refunds may be issued at our discretion
            in cases of:
          </p>
          <ul>
            <li>Service unavailability or technical failures on our end</li>
            <li>Billing errors or duplicate charges</li>
            <li>Extenuating circumstances reviewed on a case-by-case basis</li>
          </ul>
          <p>
            All refund requests must be submitted to{" "}
            <a href="mailto:contact@somigo.io">contact@somigo.io</a> within 7
            days of the charge.
          </p>

          <h3>5.3 EU Right of Withdrawal</h3>
          <p>
            If you are a consumer based in the European Union, you have the
            right to withdraw from this contract within 14 days without giving
            any reason.
          </p>
          <p>
            <strong>Waiver of Withdrawal Right:</strong> By starting the free
            trial or activating your subscription, you expressly request
            immediate access to the digital service and acknowledge that your
            right of withdrawal is waived once the service has been fully
            performed (i.e., you have accessed and used the platform).
          </p>
          <p>
            If you wish to exercise your right of withdrawal before using the
            service, please contact us at{" "}
            <a href="mailto:contact@somigo.io">contact@somigo.io</a>.
          </p>

          <h3>5.4 Payment Processing</h3>
          <p>
            Payments are processed by Stripe, Inc. We do not store full credit
            card details on our servers. Stripe acts as an independent data
            controller for payment information and is responsible for the
            security of your payment data. By providing payment information, you
            agree to Stripe's Terms of Service and Privacy Policy.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. User Content and Data</h2>
          <h3>6.1 Your Content</h3>
          <p>
            You retain all ownership rights to content you create, upload, or
            store in the Service ("User Content"). By using the Service, you
            grant us a limited license to:
          </p>
          <ul>
            <li>Store, process, and display your User Content</li>
            <li>Provide the Service to you and your team members</li>
            <li>
              Make backups and perform technical operations necessary for the
              Service
            </li>
          </ul>

          <h3>6.2 Data Backup and Loss</h3>
          <p>
            While we perform regular backups, you are responsible for
            maintaining your own backup copies of important data. We are not
            liable for any loss or corruption of User Content.
          </p>

          <h3>6.3 Prohibited Content</h3>
          <p>You agree not to upload or share content that:</p>
          <ul>
            <li>Violates laws or regulations</li>
            <li>Infringes on intellectual property rights</li>
            <li>Contains malware, viruses, or harmful code</li>
            <li>Is defamatory, obscene, or harassing</li>
            <li>Violates the privacy or rights of others</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>7. Acceptable Use Policy</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any illegal or unauthorized purpose</li>
            <li>
              Attempt to gain unauthorized access to our systems or networks
            </li>
            <li>Interfere with or disrupt the Service</li>
            <li>Use automated systems (bots, scrapers) without permission</li>
            <li>Resell or redistribute the Service without authorization</li>
            <li>Reverse engineer or attempt to extract source code</li>
            <li>Remove or modify any proprietary notices</li>
          </ul>
          <p>
            Violation of this policy may result in immediate termination of your
            account.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Intellectual Property</h2>
          <p>
            The Service, including its design, code, features, and branding, is
            owned by Happyhill and protected by copyright, trademark, and other
            intellectual property laws. You may not copy, modify, distribute, or
            create derivative works without express written permission.
          </p>

          <h3>8.1 Copyright Infringement Claims</h3>
          <p>
            We respect the intellectual property rights of others. If you
            believe that content hosted on SOMIGO infringes your copyright or
            other intellectual property rights, please contact us at{" "}
            <a href="mailto:contact@somigo.io">contact@somigo.io</a> with the
            following information:
          </p>
          <ul>
            <li>
              Identification of the copyrighted work claimed to be infringed
            </li>
            <li>
              Identification of the material that is claimed to be infringing
            </li>
            <li>Your contact information (name, address, email, phone)</li>
            <li>
              A statement that you have a good faith belief that use of the
              material is not authorized
            </li>
            <li>
              A statement that the information is accurate and that you are
              authorized to act on behalf of the copyright owner
            </li>
          </ul>
          <p>
            We will investigate all valid claims and take appropriate action,
            which may include removing the infringing content.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Service Availability and Changes</h2>
          <h3>9.1 Availability</h3>
          <p>
            We strive to provide reliable service but do not guarantee
            uninterrupted access. The Service may be unavailable due to:
          </p>
          <ul>
            <li>Scheduled maintenance (announced in advance when possible)</li>
            <li>Technical issues or server failures</li>
            <li>Events beyond our reasonable control</li>
          </ul>

          <h3>9.2 Service Modifications</h3>
          <p>
            We reserve the right to modify, suspend, or discontinue any aspect
            of the Service at any time. We will provide reasonable notice for
            significant changes that materially affect functionality.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Force Majeure</h2>
          <p>
            SOMIGO shall not be liable for any failure or delay in performance
            under these Terms due to events beyond our reasonable control,
            including but not limited to:
          </p>
          <ul>
            <li>Acts of God, natural disasters, epidemics, or pandemics</li>
            <li>War, terrorism, civil unrest, or government actions</li>
            <li>
              Internet service provider failures or major internet outages
            </li>
            <li>
              Power failures, equipment failures, or telecommunications failures
            </li>
            <li>
              Strikes, labor disputes, or shortages of labor, materials, or
              transportation
            </li>
            <li>
              Fires, floods, storms, earthquakes, or other natural calamities
            </li>
          </ul>
          <p>
            In the event of a force majeure situation, we will use reasonable
            efforts to mitigate the impact and resume normal operations as soon
            as practicable. If the force majeure event continues for more than
            30 days, either party may terminate the agreement without liability.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Privacy and Data Protection</h2>
          <p>
            Your use of the Service is also governed by our{" "}
            <a href="/legal/privacy-policy">Privacy Policy</a>, which explains
            how we collect, use, and protect your personal information. By using
            the Service, you consent to our privacy practices.
          </p>
          <p>
            For business customers and organizations, data processing is also
            governed by our{" "}
            <a href="/legal/data-processing-agreement">
              Data Processing Agreement (DPA)
            </a>
            , which complies with GDPR requirements for processing personal data
            on your behalf. By accepting these Terms, you also accept the DPA,
            which forms an integral part of our agreement with you.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Limitation of Liability</h2>
          <p>
            <strong>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, HAPPYHILL SHALL NOT BE
              LIABLE FOR:
            </strong>
          </p>
          <ul>
            <li>
              Any indirect, incidental, special, consequential, or punitive
              damages
            </li>
            <li>Loss of profits, revenue, data, or business opportunities</li>
            <li>Service interruptions or data loss</li>
            <li>
              Actions of third parties or events beyond our reasonable control
            </li>
          </ul>
          <p>
            Our total liability for any claims related to the Service shall not
            exceed the amount you paid us in the 12 months preceding the claim.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Disclaimers</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
            WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT
            LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, OR NON-INFRINGEMENT.
          </p>
          <p>
            We do not warrant that the Service will meet your requirements, be
            error-free, secure, or uninterrupted.
          </p>
        </section>

        <section className="legal-section">
          <h2>14. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Happyhill, its owners,
            employees, and affiliates from any claims, damages, losses, or
            expenses arising from:
          </p>
          <ul>
            <li>Your use of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your User Content</li>
            <li>Your violation of any rights of another party</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>15. Termination</h2>
          <h3>15.1 By You</h3>
          <p>
            You may terminate your account at any time by canceling your
            subscription through your account settings.
          </p>

          <h3>15.2 By Us</h3>
          <p>We may suspend or terminate your account immediately if you:</p>
          <ul>
            <li>Violate these Terms of Service</li>
            <li>Engage in fraudulent or illegal activity</li>
            <li>Fail to pay subscription fees</li>
            <li>Abuse or harm the Service or other users</li>
          </ul>

          <h3>15.3 Effect of Termination</h3>
          <p>
            Upon termination, your right to use the Service ceases immediately.
            We will retain your data for 30 days, after which it may be
            permanently deleted. You can request a data export before deletion.
          </p>
        </section>

        <section className="legal-section">
          <h2>16. Governing Law and Disputes</h2>
          <p>
            These Terms shall be governed by and construed in accordance with
            the laws of Denmark, without regard to its conflict of law
            provisions.
          </p>
          <p>
            Any disputes arising from these Terms or the Service shall be
            resolved through:
          </p>
          <ol>
            <li>
              <strong>Informal Negotiation:</strong> Contact us at{" "}
              <a href="mailto:contact@somigo.io">contact@somigo.io</a> to
              attempt resolution
            </li>
            <li>
              <strong>Mediation:</strong> If informal negotiation fails,
              disputes may be submitted to mediation
            </li>
            <li>
              <strong>Danish Courts:</strong> Legal proceedings shall be brought
              in the courts of Aarhus, Denmark
            </li>
          </ol>
        </section>

        <section className="legal-section">
          <h2>17. Changes to Terms</h2>
          <p>
            We may update these Terms from time to time. Material changes will
            be communicated via:
          </p>
          <ul>
            <li>Email notification to your registered email address</li>
            <li>In-app notification when you next log in</li>
            <li>Update notice on our website</li>
          </ul>
          <p>
            Continued use of the Service after changes take effect constitutes
            acceptance of the new Terms. The "Last Updated" date at the top of
            this page indicates when the Terms were last revised.
          </p>
        </section>

        <section className="legal-section">
          <h2>18. Severability</h2>
          <p>
            If any provision of these Terms is found to be unenforceable or
            invalid, that provision will be limited or eliminated to the minimum
            extent necessary, and the remaining provisions will remain in full
            force and effect.
          </p>
        </section>

        <section className="legal-section">
          <h2>19. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy, constitute the entire
            agreement between you and Happyhill regarding the Service and
            supersede all prior agreements and understandings.
          </p>
        </section>

        <section className="legal-section">
          <h2>20. Contact Information</h2>
          <p>
            If you have questions about these Terms of Service, please contact
            us:
          </p>
          <ul className="contact-list">
            <li>
              <strong>Email:</strong>{" "}
              <a href="mailto:contact@somigo.io">contact@somigo.io</a>
            </li>
            <li>
              <strong>Company:</strong> Happyhill
            </li>
            <li>
              <strong>CVR:</strong> 34028370
            </li>
            <li>
              <strong>Address:</strong> Topkærvej 7, 8200 Aarhus N, Denmark
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>21. Acknowledgment</h2>
          <p>
            BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS
            OF SERVICE, UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM.
          </p>
        </section>
      </div>
    </div>
  );
}
